// features/admin/rewardSettingsApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export interface RewardSetting {
  id: number;
  amount: string;
  points: string;
  currency: string;
  created_at?: string;
  is_default?: number;
}

export interface RewardSettingFormData {
  amount: string;
  points: string;
  currency: string;
  is_default?: boolean;
  id?: number;
}

export interface RewardSettingResponse {
  error: boolean;
  message?: string;
  data?: RewardSetting[] | RewardSetting;
}

export interface RewardSettingRequest {
  amount: string;
  points: string;
  currency: string;
  is_default?: boolean;
}

export interface UpdateRewardSettingRequest extends RewardSettingRequest {
  id: number;
}

export interface DeleteRewardSettingRequest {
  id: number;
}

export interface SetDefaultRewardSettingRequest {
  id: number;
}

// Validation helpers
export const validateAmount = (value: string): boolean => {
  const num = parseFloat(value);
  return !isNaN(num) && num > 0;
};

export const validatePoints = (value: string): boolean => {
  const num = parseFloat(value);
  return !isNaN(num) && num > 0;
};

export const rewardSettingsApi = createApi({
  reducerPath: 'rewardSettingsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT || 'https://fftestservice.sarestech.com/',
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      headers.set('Content-Type', 'application/x-www-form-urlencoded');
      return headers;
    },
  }),
  tagTypes: ['RewardSetting'],
  endpoints: (build) => ({
    listRewardSettings: build.query<RewardSettingResponse, void>({
      query: () => ({
        url: 'user/rewardsettinglist',
        method: 'POST',
        body: new URLSearchParams(),
      }),
      providesTags: ['RewardSetting'],
      transformResponse: (response: any) => ({
        error: response.error || false,
        message: response.message || '',
        data: response.data || [],
      }),
    }),

    addRewardSetting: build.mutation<RewardSettingResponse, RewardSettingRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('amount', data.amount.trim());
        formData.append('points', data.points.trim());
        formData.append('currency', data.currency.trim());
        if (data.is_default !== undefined) {
          formData.append('is_default', data.is_default ? '1' : '0');
        }

        return {
          url: 'user/rewardsettingadd',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['RewardSetting'],
    }),

    updateRewardSetting: build.mutation<RewardSettingResponse, UpdateRewardSettingRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('id', data.id.toString());
        formData.append('amount', data.amount.trim());
        formData.append('points', data.points.trim());
        formData.append('currency', data.currency.trim());
        if (data.is_default !== undefined) {
          formData.append('is_default', data.is_default ? '1' : '0');
        }

        return {
          url: 'user/rewardsettingupdate',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['RewardSetting'],
    }),

    deleteRewardSetting: build.mutation<RewardSettingResponse, DeleteRewardSettingRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('id', data.id.toString());

        return {
          url: 'user/rewardsettingdelete',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['RewardSetting'],
    }),

    setDefaultRewardSetting: build.mutation<RewardSettingResponse, SetDefaultRewardSettingRequest>({
      query: (data) => {
        const formData = new URLSearchParams();
        formData.append('id', data.id.toString());
        formData.append('is_default', '1');

        return {
          url: 'user/rewardsettingupdate',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: ['RewardSetting'],
    }),
  }),
});

export const {
  useListRewardSettingsQuery,
  useAddRewardSettingMutation,
  useUpdateRewardSettingMutation,
  useDeleteRewardSettingMutation,
  useSetDefaultRewardSettingMutation,
} = rewardSettingsApi;